import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getApiBase } from '../utils/apiConfig';
import { apiFetch } from '../utils/apiFetch';
import MoverAuthForm from './MoverAuthForm.jsx';
import MoverDashboard from './MoverDashboard.jsx';
import LoadingScreen from '../components/LoadingScreen.jsx';
import EnhancedErrorBoundary from '../components/EnhancedErrorBoundary.jsx';
import MoverLayout from '../components/MoverLayout.jsx';
import MoverBookings from './MoverBookings.jsx';
import MoverCalendar from './MoverCalendar.jsx';
import MoverProfile from './MoverProfile.jsx';
import EnhancedMoverBookings from '../components/EnhancedMoverBookings.jsx';
import EnhancedMoverCalendar from '../components/EnhancedMoverCalendar.jsx';
import EnhancedMoverProfile from '../components/EnhancedMoverProfile.jsx';
import MoverReviews from './MoverReviews.jsx';
import MoverPayments from './MoverPayments.jsx';
import MoverSettings from './MoverSettings.jsx';
import MoverTeam from './MoverTeam.jsx';
import MoverFinancials from './MoverFinancials.jsx';
import MoverPricing from './MoverPricing.jsx';
import MoverOnboardingWizard from '../features/onboarding/MoverOnboardingWizard.jsx';
const AnalyticsDashboard = lazy(() => import('../components/AnalyticsDashboard.jsx'));
const BookingChat = lazy(() => import('../components/BookingChat.jsx'));
import MoverLeadDashboard from '../components/MoverLeadDashboard.jsx';
import { 
  Box, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  StepContent,
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  TextField, 
  Button, 
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  LinearProgress,
  Alert,
  AlertTitle,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Slide,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Description as DocumentIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import SingleItemRequestForm from './SingleItemRequestForm.jsx';
import HotshotRequestForm from './HotshotRequestForm.jsx';
import HotshotBookingsPage from './HotshotRequestForm.jsx';
import HotshotAnalyticsDashboard from '../components/HotshotAnalyticsDashboard.jsx';

const requiredOnboardingSteps = [
  {
    id: 'business',
    label: 'Business Identity',
    description: 'Legal company details and contact information',
    icon: <BusinessIcon />,
  },
  {
    id: 'coverage',
    label: 'Coverage & Capacity',
    description: 'Where you move and what your crews can handle',
    icon: <LocationIcon />,
  },
  {
    id: 'pricing',
    label: 'Pricing Basics',
    description: 'Rates MOVD uses to generate accurate quotes',
    icon: <InfoIcon />,
  },
  {
    id: 'documents',
    label: 'Upload Documents',
    description: 'Upload required business licenses and insurance documents',
    icon: <DocumentIcon />,
  },
];

const serviceOptions = [
  { id: 'moving', label: 'Moving Services', description: 'Residential and commercial moving' },
];
const validServiceIds = serviceOptions.map(option => option.id);
const normalizeServices = (value) => Array.isArray(value)
  ? value.filter(service => validServiceIds.includes(service))
  : [];

function ModernOnboardingWizard({ profile, onComplete, onProfileUpdate, onLogout }) {
  const [activeStep, setActiveStep] = useState(0);
  const [services, setServices] = useState(normalizeServices(profile?.services));
  const [businessInfo, setBusinessInfo] = useState({
    companyName: profile?.name || '',
    legalName: profile?.legalName || '',
    phone: profile?.phone || '',
    address: profile?.address || '',
    email: profile?.email || '',
    website: profile?.website || '',
    businessNumber: profile?.businessNumber || '',
    licenseNumber: profile?.licenseNumber || '',
    yearsInBusiness: profile?.yearsInBusiness || '',
    description: profile?.description || '',
  });
  const [coverageInfo, setCoverageInfo] = useState({
    serviceArea: Array.isArray(profile?.serviceArea) ? profile.serviceArea.join(', ') : Array.isArray(profile?.locations) ? profile.locations.join(', ') : '',
    localMoves: profile?.localMoves !== false,
    longDistanceMoves: !!profile?.longDistanceMoves,
    residentialMoves: profile?.residentialMoves !== false,
    commercialMoves: !!profile?.commercialMoves,
    crewSize: profile?.crewSize || '',
    truckCount: profile?.truckCount || '',
    equipment: Array.isArray(profile?.equipment) ? profile.equipment.join(', ') : '',
  });
  const [pricingInfo, setPricingInfo] = useState({
    hourlyRate: profile?.hourlyRate || '',
    minimumHours: profile?.minimumHours || '',
    perMileRate: profile?.perMileRate || '',
    insurance: profile?.insurance || '',
    applicationNotes: profile?.applicationNotes || '',
  });
  const [documents, setDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validateStep = (step) => {
    console.log('Validating step:', step, 'Services:', services); // DEBUG
    const newErrors = {};
    const selectedServices = normalizeServices(services);
    
    switch (step) {
      case 0:
        if (!businessInfo.companyName.trim()) {
          newErrors.companyName = 'Company name is required';
        }
        if (!businessInfo.phone.trim()) {
          newErrors.phone = 'Phone number is required';
        }
        break;
      case 1:
        if (!coverageInfo.serviceArea.trim()) {
          newErrors.serviceArea = 'Please list at least one city or service area';
        }
        if (!coverageInfo.crewSize || Number(coverageInfo.crewSize) < 1) {
          newErrors.crewSize = 'Crew size is required';
        }
        if (coverageInfo.truckCount === '' || Number(coverageInfo.truckCount) < 0) {
          newErrors.truckCount = 'Truck count is required';
        }
        break;
      case 2:
        if (!pricingInfo.hourlyRate || Number(pricingInfo.hourlyRate) <= 0) {
          newErrors.hourlyRate = 'Hourly rate is required';
        }
        if (!pricingInfo.minimumHours || Number(pricingInfo.minimumHours) <= 0) {
          newErrors.minimumHours = 'Minimum hours is required';
        }
        if (!pricingInfo.perMileRate || Number(pricingInfo.perMileRate) <= 0) {
          newErrors.perMileRate = 'Long-distance rate is required';
        }
        break;
      case 3:
        if (documents.length === 0) {
          newErrors.documents = 'Please upload at least one document';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleServiceChange = (serviceId) => {
    console.log('Service change:', serviceId); // DEBUG
    if (!validServiceIds.includes(serviceId)) return;
    setServices(prev => {
      const selectedServices = normalizeServices(prev);
      const newServices = selectedServices.includes(serviceId)
        ? selectedServices.filter(s => s !== serviceId)
        : [...selectedServices, serviceId];
      console.log('New services:', newServices); // DEBUG
      return newServices;
    });
    setErrors(prev => ({ ...prev, services: undefined }));
  };

  const handleBusinessInfoChange = (e) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleCoverageInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCoverageInfo(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handlePricingInfoChange = (e) => {
    const { name, value } = e.target;
    setPricingInfo(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    setDocuments(prev => [...prev, ...files]);
    setErrors(prev => ({ ...prev, documents: undefined }));
  };

  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    console.log('Next button clicked, activeStep:', activeStep, 'services:', services); // DEBUG
    if (!validateStep(activeStep)) {
      console.log('Validation failed, stopping'); // DEBUG
      return;
    }
    
    setSaving(true);
    try {
      if (activeStep === 0) {
        // Filter out email from businessInfo to avoid duplicate key errors
        const { email, ...businessInfoWithoutEmail } = businessInfo;
        console.log('Updating profile with business info (without email):', businessInfoWithoutEmail); // DEBUG
        await onProfileUpdate({ ...businessInfoWithoutEmail, services: ['moving'] });
      } else if (activeStep === 1) {
        await onProfileUpdate({
          ...coverageInfo,
          services: ['moving'],
          locations: coverageInfo.serviceArea,
          equipment: coverageInfo.equipment.split(',').map(item => item.trim()).filter(Boolean),
        });
      } else if (activeStep === 2) {
        await onProfileUpdate({ ...pricingInfo, services: ['moving'] });
      } else if (activeStep === 3) {
        // Upload documents to backend
        if (documents.length > 0) {
          const formData = new FormData();
          documents.forEach(f => formData.append('documents', f));
          const res = await apiFetch('/api/movers/upload-documents', {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Document upload failed');
          }
        }
      }
      
      if (activeStep < requiredOnboardingSteps.length - 1) {
        console.log('Moving to next step'); // DEBUG
        setActiveStep(prev => prev + 1);
      } else {
        console.log('Completing onboarding'); // DEBUG
        onComplete();
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      
      // Show user-friendly error message
      alert(`Error during onboarding: ${error.message}. Please try again or contact support if the issue persists.`);
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteLater = async () => {
    setSaving(true);
    try {
      // Mark onboarding as partially completed but not authorized
      await onProfileUpdate({ 
        onboardingCompleted: true, 
        authorized: false,
        onboardingSkipped: true 
      });
      onComplete();
    } catch (error) {
      console.error('Complete later error:', error);
      setErrors(prev => ({ ...prev, general: error.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const renderServiceSelection = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          What services does your company offer?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Select all services that apply to your business. You can modify these later.
        </Typography>
        
        {errors.services && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.services}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          {serviceOptions.map((option) => (
            <Grid item xs={12} sm={6} key={option.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: normalizeServices(services).includes(option.id) ? 2 : 1,
                  borderColor: normalizeServices(services).includes(option.id) ? 'primary.main' : 'divider',
                  backgroundColor: normalizeServices(services).includes(option.id) ? 'primary.50' : 'background.paper',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
                onClick={() => handleServiceChange(option.id)}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Checkbox
                      checked={normalizeServices(services).includes(option.id)}
                      onChange={() => handleServiceChange(option.id)}
                      onClick={(event) => event.stopPropagation()}
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {option.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {normalizeServices(services).length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{normalizeServices(services).length}</strong> service{normalizeServices(services).length > 1 ? 's' : ''} selected: {' '}
              {normalizeServices(services).map(s => serviceOptions.find(opt => opt.id === s)?.label).join(', ')}
            </Typography>
          </Alert>
        )}
      </Box>
    </Fade>
  );

  const renderBusinessInfo = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Tell us about your moving company
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          This becomes your MOVD application profile. Approval is required before customers can book you.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Operating Name"
              name="companyName"
              value={businessInfo.companyName}
              onChange={handleBusinessInfoChange}
              error={!!errors.companyName}
              helperText={errors.companyName}
              required
              InputProps={{
                startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Legal Business Name" name="legalName" value={businessInfo.legalName} onChange={handleBusinessInfoChange} />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={businessInfo.phone}
              onChange={handleBusinessInfoChange}
              error={!!errors.phone}
              helperText={errors.phone}
              required
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Business Number" name="businessNumber" value={businessInfo.businessNumber} onChange={handleBusinessInfoChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="License Number" name="licenseNumber" value={businessInfo.licenseNumber} onChange={handleBusinessInfoChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Years in Business" name="yearsInBusiness" type="number" value={businessInfo.yearsInBusiness} onChange={handleBusinessInfoChange} />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Business Address"
              name="address"
              value={businessInfo.address}
              onChange={handleBusinessInfoChange}
              error={!!errors.address}
              helperText={errors.address}
              required
              multiline
              rows={2}
              InputProps={{
                startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={businessInfo.email}
              onChange={handleBusinessInfoChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Website"
              name="website"
              value={businessInfo.website}
              onChange={handleBusinessInfoChange}
              placeholder="https://www.yourcompany.com"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Business Description"
              name="description"
              value={businessInfo.description}
              onChange={handleBusinessInfoChange}
              multiline
              rows={3}
              placeholder="Tell customers about your company, experience, and what makes you unique..."
            />
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  const renderCoverageCapacity = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Coverage and moving capacity</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          MOVD uses this to decide whether your company is a good fit for local and long-distance jobs.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth label="Service Areas" name="serviceArea" value={coverageInfo.serviceArea} onChange={handleCoverageInfoChange} error={!!errors.serviceArea} helperText={errors.serviceArea || 'Example: Calgary, Edmonton, Red Deer, Vancouver Island'} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Typical Crew Size" name="crewSize" type="number" value={coverageInfo.crewSize} onChange={handleCoverageInfoChange} error={!!errors.crewSize} helperText={errors.crewSize} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Moving Trucks Available" name="truckCount" type="number" value={coverageInfo.truckCount} onChange={handleCoverageInfoChange} error={!!errors.truckCount} helperText={errors.truckCount} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Equipment" name="equipment" value={coverageInfo.equipment} onChange={handleCoverageInfoChange} helperText="Example: 5-ton truck, dollies, moving blankets, straps, piano board" />
          </Grid>
          <Grid item xs={12}>
            <FormGroup row>
              <FormControlLabel control={<Checkbox checked={coverageInfo.residentialMoves} onChange={handleCoverageInfoChange} name="residentialMoves" />} label="Residential moves" />
              <FormControlLabel control={<Checkbox checked={coverageInfo.commercialMoves} onChange={handleCoverageInfoChange} name="commercialMoves" />} label="Commercial moves" />
              <FormControlLabel control={<Checkbox checked={coverageInfo.localMoves} onChange={handleCoverageInfoChange} name="localMoves" />} label="Local moves" />
              <FormControlLabel control={<Checkbox checked={coverageInfo.longDistanceMoves} onChange={handleCoverageInfoChange} name="longDistanceMoves" />} label="Long-distance moves" />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  const renderPricingBasics = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Pricing basics</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          These rates help MOVD generate realistic quotes. You can refine detailed pricing after approval.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Hourly Rate" name="hourlyRate" type="number" value={pricingInfo.hourlyRate} onChange={handlePricingInfoChange} error={!!errors.hourlyRate} helperText={errors.hourlyRate || 'Crew + truck hourly rate'} required />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Minimum Hours" name="minimumHours" type="number" value={pricingInfo.minimumHours} onChange={handlePricingInfoChange} error={!!errors.minimumHours} helperText={errors.minimumHours} required />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Long-distance Rate" name="perMileRate" type="number" value={pricingInfo.perMileRate} onChange={handlePricingInfoChange} error={!!errors.perMileRate} helperText={errors.perMileRate || 'Per mile for now'} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Insurance Coverage" name="insurance" value={pricingInfo.insurance} onChange={handlePricingInfoChange} helperText="Example: $2M commercial liability, cargo coverage" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Application Notes" name="applicationNotes" value={pricingInfo.applicationNotes} onChange={handlePricingInfoChange} multiline rows={3} placeholder="Tell MOVD about your team, specialties, service quality, reviews, or routes you handle well." />
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  const renderDocumentUpload = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Upload Required Documents
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Upload your business license, insurance certificate, and any moving-company verification documents.
        </Typography>
        
        {errors.documents && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.documents}
          </Alert>
        )}
        
        <Paper
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: 'divider',
            backgroundColor: 'background.default',
            textAlign: 'center',
            mb: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'primary.50',
            },
          }}
          component="label"
        >
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Drop files here or click to browse
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Accepted formats: PDF, JPG, PNG (Max 10MB each)
          </Typography>
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleDocumentUpload}
            style={{ display: 'none' }}
          />
        </Paper>
        
        {documents.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Uploaded Documents ({documents.length})
            </Typography>
            <List>
              {documents.map((doc, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.name}
                    secondary={`${(doc.size / 1024 / 1024).toFixed(2)} MB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => removeDocument(index)}
                      color="error"
                    >
                      <CloseIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Fade>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBusinessInfo();
      case 1:
        return renderCoverageCapacity();
      case 2:
        return renderPricingBasics();
      case 3:
        return renderDocumentUpload();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 3, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            MOVD Partner Application
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Apply as a verified moving company. You can access the portal now, but bookings require MOVD approval.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={(activeStep / (requiredOnboardingSteps.length - 1)) * 100}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Box>
        </Box>

        {/* Stepper */}
        <Box sx={{ p: 3, backgroundColor: 'background.default' }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {requiredOnboardingSteps.map((step, index) => (
              <Step key={step.id}>
                <StepLabel
                  StepIconComponent={({ active, completed }) => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: completed ? 'success.main' : active ? 'primary.main' : 'action.disabled',
                        color: completed || active ? 'white' : 'text.secondary',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {completed ? <CheckIcon /> : step.icon}
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          {renderStepContent()}
        </Box>

        {/* Actions */}
        <Box sx={{ p: 3, backgroundColor: 'background.default', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            variant="outlined"
          >
            Back
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={onLogout}
              variant="text"
              disabled={saving}
              color="inherit"
            >
              Switch Account
            </Button>
            {/* Complete Later button - show on document upload step */}
            {activeStep === requiredOnboardingSteps.length - 1 && (
              <Button
                onClick={handleCompleteLater}
                variant="outlined"
                disabled={saving}
                color="secondary"
              >
                Complete Later
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
              sx={{ minWidth: 120 }}
            >
              {saving ? 'Saving...' : activeStep === requiredOnboardingSteps.length - 1 ? 'Complete Setup' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function MoverPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginRoute = location.pathname.endsWith('/login');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);

  useEffect(() => {
    if (!isLoginRoute) return;
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setToken(null);
    setProfile(null);
    setOnboardingCompleted(false);
    setLoading(false);
  }, [isLoginRoute]);

  // Check if user is admin and redirect immediately
  useEffect(() => {
    if (isLoginRoute) return;
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        if (userData.role === 'admin') {
          console.log('Admin detected in MoverPortal, redirecting to admin dashboard...');
          navigate('/admin-portal');
          return;
        }
      } catch (error) {
        console.error('Error parsing userData in MoverPortal:', error);
      }
    }
  }, [navigate, isLoginRoute]);

  useEffect(() => {
    if (isLoginRoute) {
      setLoading(false);
      return;
    }
    if (token) {
      // Check if user is admin before fetching mover profile
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        try {
          const userData = JSON.parse(storedUserData);
          if (userData.role === 'admin') {
            console.log('Admin user detected, skipping mover profile fetch');
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error parsing userData:', error);
        }
      }
      
      // Only fetch mover profile for non-admin users
      fetchMoverProfile();
    } else {
      setLoading(false);
    }
  }, [token, isLoginRoute]);

  const fetchMoverProfile = async () => {
    try {
      console.log('MoverPortal: fetchMoverProfile starting with token:', token); // DEBUG
      setLoading(true);
      const response = await apiFetch('/api/movers/me', {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      console.log('MoverPortal: Profile fetch response status:', response.status); // DEBUG
      console.log('MoverPortal: Profile fetch response:', response); // DEBUG
      
      if (response.ok) {
        const data = await response.json();
        console.log('MoverPortal: Profile data received:', data); // DEBUG
        const moverProfile = data.moverCompany || data.mover || data;
        setProfile(moverProfile);
        setOnboardingCompleted(moverProfile?.onboardingCompleted || false);
        console.log('MoverPortal: Profile set successfully:', moverProfile); // DEBUG
      } else {
        const errorText = await response.text();
        console.error('MoverPortal: Profile fetch failed:', response.status, errorText); // DEBUG
        handleLogout();
      }
    } catch (error) {
      console.error('MoverPortal: Profile fetch error:', error); // DEBUG
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = (newToken, userData) => {
    console.log('=== MoverPortal: handleAuthSuccess START ===');
    console.log('MoverPortal: Token received:', newToken ? 'YES' : 'NO');
    console.log('MoverPortal: User data received:', userData);
    console.log('MoverPortal: User role:', userData?.role);
    
    // Check if user is admin and redirect to combined admin dashboard
    if (userData && userData.role === 'admin') {
      console.log('🚨 ADMIN DETECTED! Starting redirect process...');
      setToken(newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      
      console.log('💾 Data saved to localStorage');
      console.log('🔄 Initiating redirect to /admin-portal');
      
      navigate('/admin-portal', { replace: true });
      return;
    }
    
    console.log('👤 Regular mover login detected, continuing normal flow...');
    
    // Regular mover login flow
    setToken(newToken);
    localStorage.setItem('token', newToken);
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
    navigate('/mover-portal/dashboard', { replace: true });
    console.log('MoverPortal: Token set, will trigger profile fetch'); // DEBUG
  };

  const handleLogout = () => {
    setToken(null);
    setProfile(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    navigate('/mover-portal');
  };

  const handleProfileUpdate = async (updates) => {
    try {
      const apiUrl = getApiBase();
      console.log('[DEBUG] Profile update request:', {
        url: `${apiUrl}/movers/me`,
        updates,
        token: token ? 'present' : 'missing'
      });
      
      const response = await apiFetch('/api/movers/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(updates),
      });

      console.log('[DEBUG] Profile update response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        console.log('[DEBUG] Profile update success:', updatedProfile);
        setProfile(updatedProfile.moverCompany || updatedProfile);
        return updatedProfile;
      } else {
        let errorData;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          errorData = { message: await response.text() };
        }
        
        console.error('[DEBUG] Profile update failed:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        
        throw new Error(`Profile update failed: ${response.status} ${response.statusText}${errorData?.error ? ` - ${errorData.error}` : ''}`);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      
      // Check if it's a network error
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      
      // Check if it's an authentication error
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      throw error;
    }
  };

  const handleOnboardingComplete = async () => {
    try {
      await handleProfileUpdate({ onboardingCompleted: true });
      setOnboardingCompleted(true);
    } catch (error) {
      console.error('Onboarding completion error:', error);
    }
  };

  // Show loading screen
  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'background.default',
      }}>
        <LoadingScreen />
      </Box>
    );
  }

  // Show auth form if not authenticated
  if (!token || isLoginRoute) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <MoverAuthForm onAuth={handleAuthSuccess} />
      </Box>
    );
  }

  // Check for admin user and redirect immediately
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  if (userData.role === 'admin') {
    console.log('👑 Admin detected - redirecting to admin portal');
    return <Navigate to="/admin-portal" replace />;
  }

  // Show onboarding if not completed (only for regular movers now)
  console.log('🎯 Onboarding check - onboardingCompleted:', onboardingCompleted);
  if (!onboardingCompleted) {
    console.log('📋 Showing onboarding wizard for regular mover');
    return (
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: 'background.default',
        py: 4,
      }}>
        <MoverOnboardingWizard
          profile={profile}
          token={token}
          onComplete={handleOnboardingComplete}
          onProfileUpdate={handleProfileUpdate}
          onLogout={handleLogout}
        />
      </Box>
    );
  }

  // Main portal interface
  return (
    <EnhancedErrorBoundary>
      <MoverLayout
        profile={profile}
        token={token}
        onLogout={handleLogout}
        loading={loading}
      >
        {/* Authorization Status Banner */}
        {profile && !profile.authorized && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2, 
              mx: 2, 
              mt: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <AlertTitle>Account Under Review</AlertTitle>
            <Typography variant="body2">
              Your mover profile is currently under review by our team. 
              {profile.onboardingSkipped && " You'll need to complete your documentation before receiving approval."}
              You can access the portal features, but customers won't be able to book your services until you're approved.
            </Typography>
            {profile.approvalStatus === 'requires_documents' && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                Action Required: Please complete your document uploads to continue the approval process.
              </Typography>
            )}
          </Alert>
        )}
        
        <Routes>
          {/* Dashboard Route */}
          <Route 
            path="dashboard" 
            element={
              profile?.services?.includes('hotshot') ? (
                <HotshotAnalyticsDashboard token={token} />
              ) : (
                <MoverDashboard 
                  token={token} 
                  onProfileUpdate={async () => {
                    await fetchMoverProfile();
                  }} 
                />
              )
            } 
          />

          {/* Service-Specific Routes */}
          {profile?.services?.includes('moving') && (
            <Route path="bookings/*" element={<EnhancedMoverBookings token={token} />} />
          )}
          
          {profile?.services?.includes('truck') && (
            <Route path="trucks" element={<Navigate to="dashboard" replace />} />
          )}
          
          {profile?.services?.includes('single') && (
            <Route path="single-item" element={<SingleItemRequestForm />} />
          )}
          
          {profile?.services?.includes('hotshot') && (
            <Route path="hotshot" element={<HotshotBookingsPage />} />
          )}

          {/* Core Routes */}
          <Route path="calendar" element={<EnhancedMoverCalendar token={token} />} />
          <Route 
            path="profile" 
            element={<EnhancedMoverProfile token={token} />} 
          />
          <Route path="pricing" element={<MoverPricing token={token} />} />
          <Route path="reviews" element={<MoverReviews token={token} />} />
          <Route path="payments" element={<MoverPayments token={token} />} />
          <Route path="financials" element={<MoverFinancials token={token} />} />
          <Route path="settings" element={<MoverSettings token={token} />} />
          <Route path="team" element={<MoverTeam token={token} />} />
          <Route path="analytics" element={<Navigate to="dashboard" replace />} />
          <Route path="leads" element={<MoverLeadDashboard token={token} />} />
          
          {/* Communication Routes */}
          <Route path="chat/:bookingId" element={
            <Suspense fallback={<LoadingScreen />}>
              <BookingChat token={token} viewerRole="mover" />
            </Suspense>
          } />
          
          {/* Default Route */}
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </MoverLayout>
    </EnhancedErrorBoundary>
  );
}

export default MoverPortal;